import { useState } from 'react';
import clsx from 'clsx';
import { Panel, Skeleton, EmptyState, Button } from '@/components/ui';
import { useModel } from '@/hooks/use-models-v2';

const TABS = ['Inputs', 'Outputs', 'Dependencies', 'Sensitivity Tests', 'Validation Rules'] as const;
type Tab = (typeof TABS)[number];

const PRIORITY_CLASSES: Record<string, string> = {
  critical: 'text-danger bg-danger/7 border-danger/13',
  high: 'text-warn bg-warn/7 border-warn/13',
  medium: 'text-blue bg-blue/7 border-blue/13',
  low: 'text-text-dim bg-input-bg border-panel-border',
};

interface ModelSchemaViewerProps {
  modelId: string;
}

export function ModelSchemaViewer({ modelId }: ModelSchemaViewerProps) {
  const [activeTab, setActiveTab] = useState<Tab>('Inputs');
  const { data, isLoading, isError, error, refetch } = useModel(modelId);
  const schema = data?.data;

  return (
    <Panel
      title="Model Schema"
      right={schema && <span className="text-xs font-mono text-text-muted">{schema.name} v{schema.version}</span>}
    >
      {/* Tab bar */}
      <div className="flex gap-px mb-3 border-b border-panel-border">
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={clsx(
              'px-3 py-1.5 text-xs font-mono tracking-[0.3px] cursor-pointer bg-transparent border-none border-b-2 transition-all duration-200',
              activeTab === tab
                ? 'text-accent border-b-accent font-semibold'
                : 'text-text-dim border-b-transparent hover:text-text-muted',
            )}
            data-testid="schema-tab"
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading && (
        <div className="flex flex-col gap-2">
          <Skeleton className="w-full" />
          <Skeleton className="w-4/5" />
          <Skeleton className="w-3/5" />
        </div>
      )}

      {isError && (
        <EmptyState
          title="FAILED TO LOAD SCHEMA"
          description={error instanceof Error ? error.message : 'An unexpected error occurred'}
          action={<Button variant="secondary" onClick={() => refetch()}>Retry</Button>}
        />
      )}

      {!isLoading && !isError && schema && (
        <div className="overflow-auto max-h-[400px]">
          {activeTab === 'Inputs' && <InputsTable inputs={schema.inputs} />}
          {activeTab === 'Outputs' && <OutputsTable outputs={schema.outputs} />}
          {activeTab === 'Dependencies' && <DependenciesTable edges={schema.dependencyGraph} />}
          {activeTab === 'Sensitivity Tests' && <SensitivityTable tests={schema.sensitivityTests} />}
          {activeTab === 'Validation Rules' && <RulesTable rules={schema.validationRules} />}
        </div>
      )}
    </Panel>
  );
}

// --- Tab content tables ---

const TH = ({ children }: { children: string }) => (
  <th className="text-left px-2 py-1.5 text-xs text-text-dim font-mono font-medium border-b border-panel-border uppercase tracking-[0.6px] whitespace-nowrap">
    {children}
  </th>
);

const TD = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <td className={clsx('px-2 py-1.5 text-sm font-mono text-text-muted', className)}>
    {children}
  </td>
);

function PriorityBadge({ priority }: { priority: string }) {
  return (
    <span className={clsx(
      'inline-block px-1.5 py-px rounded-[2px] text-micro font-mono font-semibold tracking-[0.3px] uppercase border',
      PRIORITY_CLASSES[priority] || PRIORITY_CLASSES.low,
    )}>
      {priority}
    </span>
  );
}

function InputsTable({ inputs }: { inputs: Array<{ name: string; displayName: string; dataType: string; category: string; priority: string; required: boolean }> }) {
  const sorted = [...inputs].sort((a, b) => {
    const order = { critical: 0, high: 1, medium: 2, low: 3 };
    return (order[a.priority as keyof typeof order] ?? 3) - (order[b.priority as keyof typeof order] ?? 3);
  });

  return (
    <table className="w-full border-collapse" data-testid="inputs-table">
      <thead><tr><TH>Name</TH><TH>Display Name</TH><TH>Type</TH><TH>Category</TH><TH>Priority</TH><TH>Req</TH></tr></thead>
      <tbody>
        {sorted.map(inp => (
          <tr key={inp.name} data-testid="input-row">
            <TD className="text-text-bright font-medium">{inp.name}</TD>
            <TD>{inp.displayName}</TD>
            <TD>{inp.dataType}</TD>
            <TD>{inp.category}</TD>
            <TD><PriorityBadge priority={inp.priority} /></TD>
            <TD>{inp.required ? 'Yes' : '—'}</TD>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function OutputsTable({ outputs }: { outputs: Array<{ name: string; displayName: string; dataType: string; category: string; sensitivityEligible: boolean }> }) {
  return (
    <table className="w-full border-collapse" data-testid="outputs-table">
      <thead><tr><TH>Name</TH><TH>Display Name</TH><TH>Type</TH><TH>Category</TH><TH>Sensitivity</TH></tr></thead>
      <tbody>
        {outputs.map(out => (
          <tr key={out.name}>
            <TD className="text-text-bright font-medium">{out.name}</TD>
            <TD>{out.displayName}</TD>
            <TD>{out.dataType}</TD>
            <TD>{out.category}</TD>
            <TD>{out.sensitivityEligible ? 'Yes' : '—'}</TD>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DependenciesTable({ edges }: { edges: Array<{ from: string; to: string; type: string; formula?: string }> }) {
  return (
    <table className="w-full border-collapse" data-testid="dependencies-table">
      <thead><tr><TH>From</TH><TH>To</TH><TH>Type</TH><TH>Formula</TH></tr></thead>
      <tbody>
        {edges.map((e, i) => (
          <tr key={i}>
            <TD className="text-text-bright font-medium">{e.from}</TD>
            <TD>{e.to}</TD>
            <TD>{e.type}</TD>
            <TD className="text-text-dim truncate max-w-[200px]">{e.formula || '—'}</TD>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SensitivityTable({ tests }: { tests: Array<{ id: string; name: string; inputNamedRange: string; method: string; defaultStepSize: number; steps: number }> }) {
  return (
    <table className="w-full border-collapse" data-testid="sensitivity-table">
      <thead><tr><TH>Name</TH><TH>Input</TH><TH>Method</TH><TH>Step Size</TH><TH>Steps</TH></tr></thead>
      <tbody>
        {tests.map(t => (
          <tr key={t.id}>
            <TD className="text-text-bright font-medium">{t.name}</TD>
            <TD>{t.inputNamedRange}</TD>
            <TD>{t.method}</TD>
            <TD className="tabular-nums">{t.defaultStepSize}</TD>
            <TD className="tabular-nums">{t.steps}</TD>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RulesTable({ rules }: { rules: Array<{ id: string; field: string; condition: string; severity: string; action: string; message: string }> }) {
  return (
    <table className="w-full border-collapse" data-testid="rules-table">
      <thead><tr><TH>Field</TH><TH>Condition</TH><TH>Severity</TH><TH>Action</TH><TH>Message</TH></tr></thead>
      <tbody>
        {rules.map(r => (
          <tr key={r.id}>
            <TD className="text-text-bright font-medium">{r.field}</TD>
            <TD>{r.condition}</TD>
            <TD>{r.severity}</TD>
            <TD>{r.action}</TD>
            <TD className="text-text-dim truncate max-w-[200px]">{r.message}</TD>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
